/**
 * ==========================================================================
 *  The two kinds of tent on the property, which have nothing in common.
 *
 *  The XLMOTO gazebo on the apron is a trade-stand: a flat roof on four legs,
 *  a printed band round the eaves and a logo on each wall. The harjateltat out
 *  on the field east of the apron are somebody's own tent, thirty-one of them,
 *  each a triangular prism with a groundsheet and a door.
 *
 *  A class for the same reason `Vehicles` is one: both kinds are drawn many
 *  times and their printed surfaces are expensive. The camp's flysheet is one
 *  white tile multiplied by each tent's own colour, so a field of thirty-one
 *  tents costs one texture and thirty-one material wrappers.
 *
 *  The simulation owns the groundsheet bounds and the ridge height through
 *  `campTentBox`; this only draws a flysheet over bounds it is given.
 *
 *  Rendering only. Nothing in this file may ever be imported by `src/sim`.
 * ==========================================================================
 */

import * as THREE from 'three';
import { LEIRI, TELTTA } from '../config/level';
import {
  TENT_BAND_ASPECT,
  TENT_LOGO_ASPECT,
  buildCampFly,
  buildTentBand,
  buildTentLogo,
} from './decals';
import type { SceneAssets } from './SceneAssets';
import { boxCenter, boxSize, type CampTentSpec, type SolidSpec } from '../world/Level';

/**
 * The unit triangular prism a camping tent is scaled out of: how wide its
 * floor is and how far its ridge stands over that floor, for a
 * `CylinderGeometry` of circumradius 1 with three sides round it. Written
 * down rather than inlined because `camp` divides the tent's real width and
 * height by them, and a tent scaled by the wrong one of the two is a tent
 * with the wrong pitch.
 */
const PRISM_WIDTH = Math.sqrt(3);
const PRISM_HEIGHT = 1.5;

export class Tents {
  /**
   * A camping tent and the door in it, shared by every tent in the field: a
   * unit triangular prism and the triangle that fits its gable. Three radial
   * segments is what makes a cylinder a prism and a circle a triangle.
   */
  private readonly campPrism = new THREE.CylinderGeometry(1, 1, 1, 3, 1);
  private readonly campDoor = new THREE.CircleGeometry(1, 3, Math.PI / 2);
  /** The XLMOTO tent's two printed surfaces - see `add`. */
  private tentBandMaterial: THREE.Material | null = null;
  private tentLogoMaterial: THREE.Material | null = null;
  /**
   * The camp's flysheet and the two materials cut from it, keyed by the tent's
   * own colour. One tile, thirty-one tints.
   */
  private campFlyTexture: THREE.Texture | null = null;
  private readonly campFlyMaterials = new Map<number, THREE.Material>();
  private readonly campShadeMaterials = new Map<string, THREE.Material>();

  constructor(private readonly assets: SceneAssets) {}

    /**
     * The XLMOTO pop-up on the inside of the piha's long bend.
     *
     * The collider is the sheeting: four walls, floor to eave, and that is the
     * whole of what you run into (see `TELTTA` in src/config/level.ts). What is
     * drawn here that is *not* a wall - the printed skirt and the canopy over
     * it - stands above `box.maxY`, which makes this the one thing in the yard
     * whose silhouette is taller than its collider. It can be: the eave is
     * 0.43 m over the player's own height and there is nothing beneath it to
     * climb, so the roof is scenery in the only sense that counts.
     *
     * The frame is drawn as four legs with the panels hung between them rather
     * than as one box, because a pop-up read as a box is a shipping container.
     * Six centimetres of leg at each corner is the entire difference.
     */
    add(spec: SolidSpec): THREE.Group {
      const size = boxSize(spec.box);
      const centre = boxCenter(spec.box);
      const eave = spec.box.maxY;
      const half = { x: size.x / 2, z: size.z / 2 };

      const group = new THREE.Group();
      group.name = spec.name;
      group.position.set(centre.x, 0, centre.z);

      const slab = (
        material: THREE.Material,
        sx: number,
        sy: number,
        sz: number,
        x: number,
        y: number,
        z: number,
      ): void => {
        const mesh = new THREE.Mesh(this.assets.boxGeometry, material);
        mesh.scale.set(sx, sy, sz);
        mesh.position.set(x, y, z);
        group.add(mesh);
      };

      // The four sides, as [outward normal, half-extent along that axis, the
      // width of the panel that faces that way]. Everything below hangs off this
      // list, so a tent that stops being square stays a tent.
      const sides = [
        { yaw: 0, nx: 0, nz: 1, out: half.z, width: size.x },
        { yaw: Math.PI, nx: 0, nz: -1, out: half.z, width: size.x },
        { yaw: Math.PI / 2, nx: 1, nz: 0, out: half.x, width: size.z },
        { yaw: -Math.PI / 2, nx: -1, nz: 0, out: half.x, width: size.z },
      ];

      // The legs first, at the four corners, so the panels have something to be
      // hung between.
      const leg = TELTTA.leg;
      const frame = this.assets.material('metal');
      for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
          slab(frame, leg, eave, leg, sx * (half.x - leg / 2), eave / 2, sz * (half.z - leg / 2));
        }
      }

      // Which wall is off. Compared as a normal rather than by name, because
      // that is what `sides` is already a list of - see `TELTTA.open`.
      const isOpen = (side: { nx: number; nz: number }): boolean =>
        side.nx === TELTTA.open[0] && side.nz === TELTTA.open[1];

      // Then the sheeting, flush with the collider's faces and stopping short of
      // the legs at either end. It is drawn from a couple of centimetres off the
      // ground: a tent wall reaches the grass and a skirt of it lies flat, and
      // neither of those is worth a mesh.
      //
      // Three of them, not four. The west wall came off at some point in the
      // night and the decks went in where it was; what shuts that side now is a
      // pair of speaker stacks and a trestle table, which is why the collider is
      // still the whole footprint. See `TELTTA` in src/config/level.ts, and
      // `DJ_PARTS` for the thing standing in the gap.
      const sheet = this.assets.material(spec.material);
      const panelWidth = (width: number) => width - 2 * leg;
      for (const side of sides) {
        if (isOpen(side)) continue;
        const wide = panelWidth(side.width);
        slab(
          sheet,
          side.nx !== 0 ? 0.04 : wide,
          eave - 0.02,
          side.nx !== 0 ? wide : 0.04,
          side.nx * (side.out - 0.02),
          (eave + 0.02) / 2,
          side.nz * (side.out - 0.02),
        );
      }

      // The zip down the middle of the east wall. One dark strip is all it takes
      // to say that this thing has a way in and that it is shut - which is what
      // the collider says too, and the two of them agreeing is the point.
      const seam = this.assets.material('tentCanopy');
      slab(seam, 0.05, eave - 0.14, 0.05, half.x - 0.01, (eave - 0.1) / 2, 0);

      // The big mark across each printed panel, hung proud of the sheeting like
      // the audi's rings - see `buildTentLogo` for why it is not in the tile.
      const logoWidth = Math.min(size.x, size.z) * 0.72;
      const logo = new THREE.PlaneGeometry(logoWidth, logoWidth / TENT_LOGO_ASPECT);
      for (const side of sides) {
        // Not the east wall: that one has the zip down the middle of it, and a
        // wordmark cut in half by a door is a wordmark nobody printed. And not
        // the wall that is not there, which leaves the name on the two long
        // sides the yard actually sees it from - plus the valance below, which
        // runs the whole way round whatever the walls are doing.
        if (side.nx === 1 || isOpen(side)) continue;
        const mesh = new THREE.Mesh(logo, this.tentLogo());
        mesh.position.set(side.nx * (side.out + 0.01), eave * 0.6, side.nz * (side.out + 0.01));
        mesh.rotation.y = side.yaw;
        group.add(mesh);
      }

      // The skirt, hung off the canopy's edge rather than off the wall - which
      // is where a pop-up's valance actually hangs, and why there is daylight
      // between the two of them.
      const reach = TELTTA.overhang;
      const band = this.tentBand(size.x + 2 * reach);
      for (const side of sides) {
        const mesh = new THREE.Mesh(
          new THREE.PlaneGeometry(side.width + 2 * reach, TELTTA.valance),
          band,
        );
        mesh.position.set(
          side.nx * (side.out + reach),
          eave - TELTTA.valance / 2,
          side.nz * (side.out + reach),
        );
        mesh.rotation.y = side.yaw;
        group.add(mesh);
      }

      // The canopy: a four-sided cone, turned a quarter of a face so its corners
      // land over the corners of the tent instead of over the middle of a wall.
      // A cone's radius is to its corners, hence the diagonal.
      const canopy = new THREE.ConeGeometry((size.x / 2 + reach) * Math.SQRT2, TELTTA.peak, 4);
      this.assets.scaleUv(canopy, 'tentCanopy', (size.x + 2 * reach) * 2, TELTTA.peak * 1.6);
      const roof = new THREE.Mesh(canopy, this.assets.material('tentCanopy'));
      roof.rotation.y = Math.PI / 4;
      roof.position.y = eave + TELTTA.peak / 2;
      group.add(roof);

      // And its underside, because a cone has no bottom and the overhang is at
      // eye level: without this you can see the sky through the eave from three
      // metres away.
      const soffit = new THREE.Mesh(
        new THREE.PlaneGeometry(size.x + 2 * reach, size.z + 2 * reach),
        this.assets.material('tentCanopy'),
      );
      soffit.rotation.x = Math.PI / 2;
      soffit.position.y = eave;
      group.add(soffit);

      return group;
    }

    /**
     * The printed band, sized to run whole logos round a tent of this width.
     *
     * One texture for the whole level: the repeat count is a property of the
     * texture rather than of the mesh, so two tents of different widths would
     * need two of these. There is one tent.
     */
    tentBand(width: number): THREE.Material {
      if (!this.tentBandMaterial) {
        const map = buildTentBand();
        // Whole repeats only, or the seam between the last logo and the first
        // one lands in the middle of a word.
        map.repeat.set(Math.max(1, Math.round(width / (TELTTA.valance * TENT_BAND_ASPECT))), 1);
        this.tentBandMaterial = new THREE.MeshLambertMaterial({ map, side: THREE.DoubleSide });
      }
      return this.tentBandMaterial;
    }

    tentLogo(): THREE.Material {
      if (!this.tentLogoMaterial) {
        this.tentLogoMaterial = new THREE.MeshLambertMaterial({
          map: buildTentLogo(),
          transparent: true,
          side: THREE.FrontSide,
        });
      }
      return this.tentLogoMaterial;
    }

    /**
     * The camp on the field east of the apron: everybody who slept in their own
     * tent rather than under a table.
     *
     * A harjateltta is a triangular prism lying on one of its faces, and that is
     * one `CylinderGeometry` with three sides round it - the two slopes and the
     * floor are its sides, and the gable at each end is a cap it comes with. So
     * the whole tent is one shared geometry, one rotation and one scale, and a
     * field of thirty-one of them costs three meshes each.
     *
     * The scale is the part worth reading twice. A prism cut from an equilateral
     * triangle has a pitch of sixty degrees, which is not the pitch anybody's
     * tent was sold with, so width and height are divided by the unit prism's
     * own two numbers separately: `PRISM_WIDTH` across the floor and
     * `PRISM_HEIGHT` from the floor to the ridge. Then the axis is laid down
     * along the ridge, which is what `-PI/2` about x does - it takes the lone
     * edge at local +z to +y and the flat face opposite it to the ground.
     *
     * The simulation owns the matching groundsheet bounds and ridge height
     * through `campTentBox`; this only draws the flysheet over those bounds.
     */
    camp(specs: readonly CampTentSpec[]): THREE.Group[] {
      return specs.map((spec) => {
        const group = new THREE.Group();
        group.name = spec.name;
        group.position.set(spec.center[0], 0, spec.center[1]);
        // The door faces `rotationY`, and the ridge runs the same way, because
        // the door is in a gable and a gable is the end of a ridge.
        group.rotation.y = spec.rotationY;

        const fly = new THREE.Mesh(this.campPrism, this.campFlyMaterial(spec.color));
        fly.rotation.x = -Math.PI / 2;
        fly.scale.set(spec.width / PRISM_WIDTH, spec.length, spec.height / PRISM_HEIGHT);
        // The unit prism's floor sits half a radius under its own centre, so
        // this is what stands the tent on the grass rather than in it.
        fly.position.y = (spec.height / PRISM_HEIGHT) / 2;
        group.add(fly);

        // The groundsheet under the hem, a shade of the fly's own colour: a
        // little wider than the tent all round, and low enough to read as a
        // pegged edge rather than as a plinth somebody stood a tent on.
        const hem = new THREE.Mesh(this.assets.boxGeometry, this.campShadeMaterial(spec.color, LEIRI.hem.shade));
        hem.scale.set(
          spec.width + 2 * LEIRI.hem.reach,
          LEIRI.hem.height,
          spec.length + 2 * LEIRI.hem.reach,
        );
        hem.position.y = LEIRI.hem.height / 2;
        group.add(hem);

        // And the door, a centimetre proud of the gable it is zipped into.
        const door = new THREE.Mesh(this.campDoor, this.campShadeMaterial(spec.color, LEIRI.doorShade));
        const doorWidth = (spec.width * LEIRI.door) / PRISM_WIDTH;
        const doorHeight = (spec.height * LEIRI.door) / PRISM_HEIGHT;
        door.scale.set(doorWidth, doorHeight, 1);
        door.position.set(0, doorHeight / 2, spec.length / 2 + 0.01);
        group.add(door);

        return group;
      });
    }

    /**
     * A tent's flysheet: one white tile multiplied by the tent's own colour.
     *
     * The one place in the renderer where a texture is tinted, and it is
     * deliberate - see `buildCampFly` for why a campsite cannot have a palette
     * entry per tent. Cached by colour, so ten colours cost ten materials and
     * one canvas however many tents are pitched.
     */
    campFlyMaterial(color: number): THREE.Material {
      let material = this.campFlyMaterials.get(color);
      if (!material) {
        this.campFlyTexture ??= buildCampFly();
        material = new THREE.MeshLambertMaterial({ color, map: this.campFlyTexture });
        this.campFlyMaterials.set(color, material);
      }
      return material;
    }

    /**
     * The two shades of a tent that are not the fly: the doorway and the hem
     * under it. Untextured, because both are a shadow rather than a surface -
     * and both are the tent's own colour taken down, so a red tent has a red
     * door and never a grey one.
     */
    campShadeMaterial(color: number, shade: number): THREE.Material {
      const key = `${color}|${shade}`;
      let material = this.campShadeMaterials.get(key);
      if (!material) {
        material = new THREE.MeshLambertMaterial({
          color: new THREE.Color(color).multiplyScalar(shade),
        });
        this.campShadeMaterials.set(key, material);
      }
      return material;
    }
}
